import type { KftDynamicContentMetrics } from '~/utils/kftApiUtils'
import { formattedNumberOfEvents } from '~/utils/formatNumber'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'

// Function to replace placeholders with actual values
export const replacePlaceholders = (
  str: string,
  metrics: KftDynamicContentMetrics,
  locale?: string
): string => {
  return str.replace(/{(\w+)}/g, (match, key) => {
    const metric = metrics[key as keyof KftDynamicContentMetrics]
    const shouldReplace = metric !== undefined && metric !== null
    if (shouldReplace) {
      const value = metrics[key as keyof KftDynamicContentMetrics]
      // Format numbers (like nrEvents) using locale if provided
      if (typeof value === 'number' && locale) {
        // Convert locale to BCP47 format (en-us -> en-US, de-de -> de-DE) for proper formatting
        const formattedLocale = convertLocaleIntoBCP47(locale)
        return formattedNumberOfEvents(formattedLocale, value)
      }
      return String(value)
    }
    return match
  })
}

// Recursive function to traverse and update all string fields, excluding metadata
export const updateObjectWithMetrics = <T extends Record<string, any>>(
  obj: T,
  metrics: KftDynamicContentMetrics,
  metadataFields: string[],
  locale?: string
) => {
  for (const key in obj) {
    if (metadataFields?.includes(key)) {
      // Skip metadata fields
      continue
    }

    if (typeof obj[key] === 'string') {
      // Replace placeholders in string fields with locale support
      obj[key] = replacePlaceholders(obj[key], metrics, locale) as T[typeof key]
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      // Recurse into nested objects
      updateObjectWithMetrics(obj[key] as T, metrics, metadataFields, locale)
    }
  }
}
export const dynamicContentTransformer = <T extends Record<string, any>>(
  data: T,
  metrics: KftDynamicContentMetrics,
  locale?: string
): T => {
  // List of metadata fields to exclude from replacement
  const metadataFields = [
    'createdAt',
    'updatedAt',
    'publishedAt',
    'slug',
    'locale',
    'metaImage',
    'id',
    'image',
    'localizations',
  ]
  const obj = data
  updateObjectWithMetrics(obj, metrics, metadataFields, locale)

  return obj
}
